import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ShieldCheck, CheckCircle2, Lock, IndianRupee } from "lucide-react";

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
];

declare global {
  interface Window {
    Razorpay: any;
  }
}

const loadRazorpayScript = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageTitle: string;
  /** amount in INR (whole rupees). If undefined, allows custom amount entry. */
  amount?: number;
  isCustom?: boolean;
}

const PaymentDialog = ({
  open,
  onOpenChange,
  packageTitle,
  amount,
  isCustom = false,
}: PaymentDialogProps) => {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [reference, setReference] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<{ paymentId: string } | null>(null);

  useEffect(() => {
    if (!open) {
      setSuccess(null);
      setLoading(false);
    }
  }, [open]);

  const reset = () => {
    setName("");
    setEmail("");
    setPhone("");
    setReference("");
    setCustomAmount("");
    setSuccess(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalAmount = isCustom ? Number(customAmount) : amount ?? 0;

    if (!name.trim() || name.trim().length < 2) {
      toast({ title: "Please enter your full name", variant: "destructive" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({ title: "Please enter a valid email", variant: "destructive" });
      return;
    }
    if (!/^\d{6,15}$/.test(phone)) {
      toast({ title: "Please enter a valid phone number", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(finalAmount) || finalAmount < 100 || finalAmount > 1000000) {
      toast({
        title: "Invalid amount",
        description: "Amount must be between ₹100 and ₹10,00,000",
        variant: "destructive",
      });
      return;
    }
    if (isCustom && reference.trim().length < 3) {
      toast({ title: "Please enter a reference / purpose", variant: "destructive" });
      return;
    }

    setLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Could not load secure payment gateway. Please try again.");
      }

      const fullPhone = `${countryCode} ${phone}`;
      const titleForOrder = isCustom
        ? `Custom Payment — ${reference.trim()}`
        : packageTitle;

      const { data, error } = await supabase.functions.invoke(
        "razorpay-create-order",
        {
          body: {
            amount: finalAmount,
            currency: "INR",
            packageTitle: titleForOrder,
            customerName: name.trim(),
            customerEmail: email.trim(),
            customerPhone: fullPhone,
            notes: isCustom ? reference.trim() : undefined,
          },
        },
      );

      if (error || !data?.orderId) {
        throw new Error(data?.error || error?.message || "Could not create order");
      }

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "MViBE",
        description: titleForOrder,
        order_id: data.orderId,
        prefill: {
          name: name.trim(),
          email: email.trim(),
          contact: fullPhone,
        },
        theme: { color: "#371134" },
        modal: {
          ondismiss: () => setLoading(false),
        },
        handler: async (response: any) => {
          try {
            const { data: verifyData, error: verifyError } =
              await supabase.functions.invoke("razorpay-verify-payment", {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              });

            if (verifyError || !verifyData?.verified) {
              throw new Error(verifyData?.error || "Payment verification failed");
            }

            setSuccess({ paymentId: response.razorpay_payment_id });
            toast({
              title: "Payment successful 🎉",
              description: "Thank you! Mahirah will reach out within 24 hours.",
            });
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Verification failed";
            toast({ title: "Verification failed", description: msg, variant: "destructive" });
          } finally {
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp: any) => {
        toast({
          title: "Payment failed",
          description: resp?.error?.description || "Please try again.",
          variant: "destructive",
        });
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Could not start payment", description: msg, variant: "destructive" });
      setLoading(false);
    }
  };

  const displayAmount = isCustom ? Number(customAmount) || 0 : amount ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="text-center py-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
              Payment Successful
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Thank you for choosing MViBE. Mahirah will personally reach out to you within 24 hours to begin your journey.
            </p>
            <div className="bg-muted/60 rounded-lg p-3 mb-5 text-xs text-muted-foreground">
              Payment ID: <span className="font-mono text-foreground">{success.paymentId}</span>
            </div>
            <Button onClick={() => handleClose(false)} className="w-full rounded-xl">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {isCustom ? "Custom Payment" : "Complete Your Payment"}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {isCustom
                  ? "Securely pay your agreed amount via Razorpay."
                  : packageTitle}
              </DialogDescription>
            </DialogHeader>

            {/* Amount display */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/10 border border-primary/15 rounded-xl p-4 my-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                {isCustom ? "Amount to pay" : "Total"}
              </p>
              {isCustom ? (
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min={100}
                    max={1000000}
                    step={1}
                    inputMode="numeric"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value.replace(/[^\d]/g, ""))}
                    className="pl-9 text-lg font-bold bg-background"
                    required
                  />
                </div>
              ) : (
                <p className="text-3xl font-heading font-bold text-primary">
                  ₹{displayAmount.toLocaleString("en-IN")}
                </p>
              )}
            </div>

            <form onSubmit={handlePay} className="space-y-4">
              {isCustom && (
                <div>
                  <Label htmlFor="ref">Purpose / Reference</Label>
                  <Input
                    id="ref"
                    placeholder="e.g. Coaching retainer — Apr 2026"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    maxLength={120}
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="pay-name">Full Name</Label>
                <Input
                  id="pay-name"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <Label htmlFor="pay-email">Email</Label>
                <Input
                  id="pay-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>

              <div>
                <Label htmlFor="pay-phone">Phone</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="pay-phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    maxLength={15}
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full rounded-xl text-base font-semibold bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Pay {displayAmount > 0 ? `₹${displayAmount.toLocaleString("en-IN")}` : ""} Securely
                  </>
                )}
              </Button>

              <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                256-bit secure payment via Razorpay
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
